// Google Calendar API Configuration
const BASE_URL = 'https://www.googleapis.com/calendar/v3';

class GoogleCalendarService {
  constructor() {
    this.isInitialized = true; // No specific initialization needed for REST
  }

  // Helper to get the access token
  getAccessToken() {
    return localStorage.getItem('authToken');
  }

  // Helper for making authenticated requests
  async request(endpoint, options = {}) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('User not signed in');
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `API Error: ${response.status}`);
    }

    return response.json();
  }

  // Initialize (kept for compatibility, but does nothing now)
  async initialize() {
    return Promise.resolve();
  }

  // Sign in (delegates to SSO service or just checks token)
  async signIn() {
    // In this new architecture, sign-in is handled by ssoService
    // This method just checks if we have a token
    if (this.getAccessToken()) {
      return true;
    }
    throw new Error('Please sign in using the main Sign In button');
  }

  // Sign out
  async signOut() {
    // Import ssoService to clear localStorage
    const { ssoService } = await import('./ssoService');
    await ssoService.signOutFromGoogle();
    return true;
  }

  // Get current user profile (from session storage)
  getProfile() {
    const token = this.getAccessToken();
    if (!token) return null;
    
    return {
      name: localStorage.getItem('userName'),
      email: localStorage.getItem('userEmail'),
      imageUrl: localStorage.getItem('userPicture'),
    };
  }

  // Fetch events for a specific time range
  async getEvents(timeMin, timeMax, calendarId = 'primary') {
    try {
      const params = new URLSearchParams({
        timeMin: timeMin.toISOString(),
        timeMax: timeMax.toISOString(),
        singleEvents: 'true',
        orderBy: 'startTime',
        maxResults: '2500',
      });

      const data = await this.request(`/calendars/${encodeURIComponent(calendarId)}/events?${params}`);
      return data.items || [];
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  // Create a new event
  async createEvent(event, calendarId = 'primary') {
    try {
      return await this.request(`/calendars/${encodeURIComponent(calendarId)}/events`, {
        method: 'POST',
        body: JSON.stringify(event),
      });
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  // Update an existing event
  async updateEvent(eventId, event, calendarId = 'primary') {
    try {
      return await this.request(`/calendars/${encodeURIComponent(calendarId)}/events/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify(event),
      });
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // Delete an event
  async deleteEvent(eventId, calendarId = 'primary') {
    try {
      await this.request(`/calendars/${encodeURIComponent(calendarId)}/events/${eventId}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const googleCalendarService = new GoogleCalendarService();
export default googleCalendarService;
